import React, { useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Columns3, Calendar, ArrowRightLeft, Crown, Tag, Search, Plane } from "lucide-react";
import Seo from "../components/Seo";
import ShareButton from "../components/ShareButton";
import { Button } from "../components/ui/button";
import AirportPicker from "../components/AirportPicker";
import { compareCabins, airlineLogo, fmtPoints, fmtDate } from "../data/mock";
import ProGate, { useIsPro } from "../components/ProGate";

const cabinTheme = {
  economy: { ring: "border-slate-200", head: "bg-slate-100 text-slate-700", bar: "bg-slate-400" },
  premium: { ring: "border-amber-200", head: "bg-amber-100 text-amber-800", bar: "bg-amber-400" },
  business: { ring: "border-blue-200", head: "bg-blue-100 text-blue-700", bar: "bg-blue-500" },
  first: { ring: "border-purple-200", head: "bg-purple-100 text-purple-700", bar: "bg-purple-500" },
};

function CabinColumn({ c, maxPoints, isCheapest, isBestValue, from, to, date }) {
  const t = cabinTheme[c.key];
  const b = c.best;
  return (
    <div data-testid={`cabin-column-${c.key}`} className={`flex flex-col rounded-2xl border bg-white ${t.ring} ${isBestValue ? "shadow-lg shadow-blue-100 ring-2 ring-blue-500" : ""}`}>
      <div className={`flex items-center justify-between rounded-t-2xl px-4 py-3 ${t.head}`}>
        <span className="text-sm font-bold">{c.label}</span>
        <div className="flex gap-1">
          {isCheapest && <span className="flex items-center gap-1 rounded-full bg-white/80 px-2 py-0.5 text-[10px] font-bold text-green-700"><Tag className="h-3 w-3" /> Cheapest</span>}
          {isBestValue && <span className="flex items-center gap-1 rounded-full bg-white/80 px-2 py-0.5 text-[10px] font-bold text-blue-700"><Crown className="h-3 w-3" /> Best value</span>}
        </div>
      </div>
      {!b ? (
        <div className="flex flex-1 flex-col items-center justify-center px-4 py-10 text-center">
          <Plane className="h-8 w-8 text-slate-200" />
          <p className="mt-2 text-sm font-semibold text-slate-600">No {c.label.toLowerCase()} seats</p>
          <p className="text-xs text-slate-400">on this date</p>
        </div>
      ) : (
        <div className="flex flex-1 flex-col px-4 py-4">
          <p className="text-3xl font-extrabold text-slate-900" data-testid={`cabin-points-${c.key}`}>{fmtPoints(b.points)} <span className="text-xs font-semibold text-slate-500">pts</span></p>
          <p className="text-xs text-slate-500">+ ${b.taxes.amount} taxes</p>
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-100"><div className={`h-full rounded-full ${t.bar}`} style={{ width: `${Math.max(6, (b.points / maxPoints) * 100)}%` }} /></div>

          <div className="mt-4 flex items-center gap-2">
            <img src={airlineLogo(b.airlineCode)} alt={b.airline} className="h-7 w-7 rounded object-contain" onError={(e) => { e.target.style.visibility = "hidden"; }} />
            <div className="min-w-0"><p className="truncate text-sm font-semibold text-slate-900">{b.airline}</p><p className="truncate text-xs text-slate-500">{b.program} · {b.seats} seat{b.seats > 1 ? "s" : ""}</p></div>
          </div>

          <dl className="mt-4 space-y-1.5 border-t border-slate-100 pt-3 text-xs">
            <div className="flex justify-between"><dt className="text-slate-500">Est. cash fare</dt><dd className="font-semibold text-slate-900">${new Intl.NumberFormat().format(c.cash)}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Value per point</dt><dd className={`font-bold ${isBestValue ? "text-blue-600" : "text-slate-900"}`} data-testid={`cabin-cpp-${c.key}`}>{c.cpp}¢</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Other options</dt><dd className="font-semibold text-slate-900">{c.options.length - 1}</dd></div>
          </dl>

          {c.options.length > 1 && (
            <ul className="mt-3 space-y-1">
              {c.options.slice(1, 3).map((o) => (
                <li key={o.id} className="flex items-center justify-between rounded-md bg-slate-50 px-2 py-1 text-[11px] text-slate-600"><span className="truncate">{o.airline}</span><span className="font-semibold">{fmtPoints(o.points)}</span></li>
              ))}
            </ul>
          )}

          <div className="mt-auto flex items-center justify-between pt-4">
            <Link to={`/search?from=${from}&to=${to}&date=${date}&flex=3`} className="text-xs font-semibold text-blue-600 hover:underline">View all</Link>
            <ShareButton deal={b} />
          </div>
        </div>
      )}
    </div>
  );
}

function CompareForm({ initial, onSubmit }) {
  const [form, setForm] = useState(initial);
  const submit = () => { if (form.from.length === 3 && form.to.length === 3) onSubmit(form); };
  const swap = () => setForm((f) => ({ ...f, from: f.to, to: f.from }));
  return (
    <div className="mt-6 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-xl shadow-slate-200/60 md:flex-row md:items-end">
      <AirportPicker label="From" value={form.from} onChange={(v) => setForm({ ...form, from: v })} testId="cabin-from-input" className="flex-1" />
      <button onClick={swap} aria-label="Swap" data-testid="cabin-swap-button" className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:text-blue-600 md:flex"><ArrowRightLeft className="h-4 w-4" /></button>
      <AirportPicker label="To" value={form.to} onChange={(v) => setForm({ ...form, to: v })} testId="cabin-to-input" className="flex-1" />
      <div className="flex-1">
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Date</label>
        <div className="flex h-11 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100">
          <Calendar className="h-4 w-4 text-slate-400" />
          <input type="date" value={form.date} data-testid="cabin-date-input" onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full bg-transparent text-sm outline-none" />
        </div>
      </div>
      <Button onClick={submit} data-testid="cabin-compare-submit" className="h-11 gap-2 bg-blue-600 px-6 font-semibold hover:bg-blue-700"><Search className="h-4 w-4" /> Compare</Button>
    </div>
  );
}

function CompareResults({ data, date, from, to, locked }) {
  const maxPoints = Math.max(...data.cabins.map((c) => c.best?.points || 0), 1);
  const bestValue = data.cabins.find((c) => c.key === data.bestValue);
  return (
    <>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-xl font-bold text-slate-900" data-testid="cabin-route-title">{data.origin.city} → {data.destination.city}</h2>
          <p className="text-sm text-slate-500">{fmtDate(date)} · {new Intl.NumberFormat().format(data.distance)} mi · {data.duration}</p>
        </div>
        {bestValue && <p className="text-sm text-slate-600">Best value: <span className="font-bold capitalize text-blue-600">{bestValue.key}</span> at {bestValue.cpp}¢ per point</p>}
      </div>
      <ProGate locked={locked} title="Cabin Compare is a PRO feature" description={`We found ${data.cabins.filter((c) => c.best).length} cabins with award seats on this date. PRO members see every cabin's points, taxes and cents-per-point value side by side, plus a full year of availability and alerts.`}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {data.cabins.map((c) => (
            <CabinColumn key={c.key} c={c} maxPoints={maxPoints} isCheapest={data.cheapest === c.key} isBestValue={data.bestValue === c.key} from={from} to={to} date={date} />
          ))}
        </div>
      </ProGate>
      <p className="mt-6 text-center text-xs text-slate-400">Cash fares are estimates based on distance and cabin; value = (cash fare − taxes) ÷ points. Verify with the airline before transferring points.</p>
    </>
  );
}

const defaultDate = () => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10); };

export default function CabinCompare() {
  const [params, setParams] = useSearchParams();
  const from = (params.get("from") || "JFK").toUpperCase();
  const to = (params.get("to") || "LHR").toUpperCase();
  const date = params.get("date") || defaultDate();
  const isPro = useIsPro();
  const locked = !isPro;
  const data = useMemo(() => (from.length === 3 && to.length === 3 ? compareCabins(from, to, date) : null), [from, to, date]);

  return (
    <main className="min-h-[70vh] bg-slate-50">
      <Seo title={`${from} to ${to} cabin comparison — Economy vs Business vs First | FindMyFlight`} description={`Compare award prices for every cabin from ${from} to ${to} on ${fmtDate(date)}: points, taxes and cents-per-point value side by side.`} path="/cabin-compare" />
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-5 py-8">
          <div className="flex items-center gap-2 text-blue-600"><Columns3 className="h-5 w-5" /><span className="text-sm font-semibold uppercase tracking-wide">Cabin Compare</span><span className="rounded-md bg-blue-600 px-1.5 py-0.5 text-[10px] font-bold text-white">PRO</span></div>
          <h1 className="mt-2 text-3xl font-extrabold text-slate-900">Economy vs Business vs First</h1>
          <p className="mt-2 max-w-2xl text-slate-600">Same route, same date — see what each cabin costs in points and which one squeezes the most value out of your miles.</p>
          <CompareForm key={`${from}-${to}-${date}`} initial={{ from, to, date }} onSubmit={(f) => setParams({ from: f.from, to: f.to, date: f.date })} />
        </div>
      </div>
      <div className="mx-auto max-w-6xl px-5 py-8">
        {data ? <CompareResults data={data} date={date} from={from} to={to} locked={locked} /> : <p className="text-center text-slate-500">Enter two 3-letter airport codes to compare cabins.</p>}
      </div>
    </main>
  );
}
